import {Field2} from "@mytour/ui/form";
import type React from "react";
import {isMobile} from "react-device-detect";
import type {UseFormReturn} from "react-hook-form";
import {FormGroup, Input, Table} from "reactstrap";
import z from "zod";

const piedSchema = z.object({
  fourchetteEpine: z.number().min(1).max(9).nullable(),
  sole: z.number().min(1).max(9).nullable(),
  talon: z.number().min(1).max(9).nullable(),
  barres: z.number().min(1).max(9).nullable(),
  quartiers: z.number().min(1).max(9).nullable(),
  pince: z.number().min(1).max(9).nullable(),
  cartilagesCoussinet: z.number().min(1).max(9).nullable(),
  pouls: z.number().min(0).max(3).nullable(),
  pinceSonder: z.number().min(0).max(3).nullable(),
});

export const step3Schema = z.object({
  AD: piedSchema,
  AG: piedSchema,
  PD: piedSchema,
  PG: piedSchema,
  hideSpectrum: z.boolean(),
});

export type Step3Form = z.infer<typeof step3Schema>;

type Props = {
  hookForm: UseFormReturn<Step3Form>;
  disabled: boolean;
};

const CRITERIA = [
  {id: "fourchetteEpine", label: "Fourchette"},
  {id: "sole", label: "Sole"},
  {id: "talon", label: "Talon"},
  {id: "barres", label: "Barres"},
  {id: "quartiers", label: "Quartiers"},
  {id: "pince", label: "Pince"},
  {id: "cartilagesCoussinet", label: "Cartilages"},
] as const;

const CRITERIA2 = [
  {id: "pouls", label: "Pouls /3"},
  {id: "pinceSonder", label: "Pince Sonder /3"},
] as const;

const HOOVES = [
  {id: "AD", label: "AD"},
  {id: "AG", label: "AG"},
  {id: "PD", label: "PD"},
  {id: "PG", label: "PG"},
] as const;

export function Step3({hookForm, disabled}: Props) {
  const {register, watch, setValue} = hookForm;

  const hideSpectrum = watch("hideSpectrum");
  const {onBlur, ref} = register("hideSpectrum");

  function handleSpectrum(_e: React.ChangeEvent<HTMLInputElement>) {
    if (hideSpectrum) {
      setValue("hideSpectrum", false);
    } else {
      setValue("hideSpectrum", true);
    }
  }

  return (
    <>
      <p style={{marginBottom: "24px"}}>
        <strong>Évaluation des pieds</strong>
      </p>
      <FormGroup>
        <div className="form-check form-switch">
          Masquer sur le compte rendu
          <Input
            type="checkbox"
            name="hideSpectrum"
            id="hideSpectrum"
            onBlur={onBlur}
            innerRef={ref}
            checked={hideSpectrum}
            onChange={handleSpectrum}
            label=""
            disabled={disabled}
          />
        </div>
      </FormGroup>
      <Table responsive bordered>
        <thead>
          <tr>
            <th>Critères</th>
            {HOOVES.map((hoof) => (
              <th key={hoof.id}>{hoof.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {CRITERIA.map((criterion) => (
            <tr key={criterion.id}>
              <td>{criterion.label}</td>
              {HOOVES.map((hoof) => (
                <td key={`${hoof.id}-${criterion.id}`} style={{width: "100px"}}>
                  <Field2
                    noLabel
                    name={`${hoof.id}.${criterion.id}`}
                    type="number"
                    min={1}
                    max={9}
                    hookForm={hookForm}
                    disabled={disabled || hideSpectrum}
                    style={{width: isMobile ? "35px" : "52px"}}
                  />
                </td>
              ))}
            </tr>
          ))}
          {CRITERIA2.map((criterion) => (
            <tr key={criterion.id}>
              <td>{criterion.label}</td>
              {HOOVES.map((hoof) => (
                <td key={`${hoof.id}-${criterion.id}`}>
                  <Field2
                    noLabel
                    name={`${hoof.id}.${criterion.id}`}
                    type="number"
                    min={0}
                    max={3}
                    hookForm={hookForm}
                    disabled={disabled || hideSpectrum}
                    style={{width: isMobile ? "35px" : "52px"}}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}
